import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, DateDecoder, EnumDecoder, field, IntegerDecoder, StringDecoder } from '@common/encoding';
import { Formatter, STMath } from '@stamhoofd/utility';
import { v4 as uuidv4 } from 'uuid';
import { PaymentCustomer } from '../PaymentCustomer.js';
import { PaymentSettlementDetailed } from '../settlements/PaymentSettlement.js';
import { downgradePaymentMethodV150, PaymentMethod, PaymentMethodHelper, PaymentMethodV150 } from '../PaymentMethod.js';
import { PaymentProvider } from '../PaymentProvider.js';
import { PaymentStatus } from '../PaymentStatus.js';
import { PaymentType, PaymentTypeHelper } from '../PaymentType.js';
import { TransferSettings } from '../webshops/TransferSettings.js';
import { upgradePriceFrom2To4DecimalPlaces } from '../upgradePriceFrom2To4DecimalPlaces.js';
export class Payment extends AutoEncoder {
    id;
    type = PaymentType.Payment;
    /// Last selected payment method. Nullable if none has been selected
    method;
    status = PaymentStatus.Created;
    provider = null;
    customer = null;
    price = 0;
    refundedAmount = 0;
    roundingAmount = 0;
    freeContribution = null;
    // Transfer description if paid via transfer
    transferDescription = null;
    transferSettings = null;
    paidAt = null;
    createdAt = new Date();
    updatedAt = new Date();
    organizationId = null;
    payingOrganizationId = null;
    invoiceId = null;
    payingUserId = null;
    /**
     * Link to the payment that is being reversed by this payment.
     * Used for refunds and chargebacks. When creating a refund, this references
     * the online payment that will be (partially) refunded via the payment provider.
     */
    reversingPaymentId = null;
    /**
     * Total price (negative, like refundedAmount) of refunds that are still pending at the
     * payment provider. Moves to refundedAmount once the provider confirms the refund.
     */
    pendingRefundAmount = 0;
    get isPending() {
        return this.status !== PaymentStatus.Succeeded && this.status !== PaymentStatus.Failed;
    }
    get isOverDue() {
        const daysToPay = 7;
        return this.isPending && this.createdAt < new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * daysToPay);
    }
    get isSucceeded() {
        return this.status === PaymentStatus.Succeeded;
    }
    get isFailed() {
        return this.status === PaymentStatus.Failed;
    }
    get canChangeStatus() {
        return this.price !== 0 && (this.method === PaymentMethod.Transfer || this.method === PaymentMethod.PointOfSale || this.method === PaymentMethod.Unknown);
    }
    get title() {
        if (this.method !== PaymentMethod.Unknown) {
            if (this.type !== PaymentType.Payment) {
                return $t(`%1z`) + ' ' + PaymentMethodHelper.getName(this.method);
            }
            return PaymentMethodHelper.getNameCapitalized(this.method);
        }
        return Formatter.capitalizeFirstLetter(PaymentTypeHelper.getName(this.type));
    }
    get theme() {
        if (this.type === PaymentType.Reallocation) {
            return 'theme-secundary';
        }
        if (this.type === PaymentType.Chargeback || this.type === PaymentType.Refund) {
            return 'theme-error';
        }
    }
    getHTMLTable() {
        let str = `<table width="100%" cellspacing="0" cellpadding="0" class="email-data-table"><tbody>`;
        /**
         * Replacement.create({
                    token: "priceToPay",
                    value: forcePayment?.status !== PaymentStatus.Succeeded ? Formatter.price(forcePayment?.price ?? 0) : ""
                }),
                Replacement.create({
                    token: "paymentMethod",
                    value: forcePayment?.method ? PaymentMethodHelper.getName(forcePayment.method) : PaymentMethodHelper.getName(this.data.paymentMethod)
                }),
                Replacement.create({
                    token: "transferDescription",
                    value: forcePayment?.status !== PaymentStatus.Succeeded && forcePayment?.method === PaymentMethod.Transfer ? (forcePayment?.transferDescription ?? "") : ""
                }),
                Replacement.create({
                    token: "transferBankAccount",
                    value: forcePayment?.status !== PaymentStatus.Succeeded && forcePayment?.method === PaymentMethod.Transfer ? ((webshop?.meta.transferSettings.iban ? webshop?.meta.transferSettings.iban : organization.meta.transferSettings.iban) ?? "") : ""
                }),
                Replacement.create({
                    token: "transferBankCreditor",
                    value: forcePayment?.status !== PaymentStatus.Succeeded && forcePayment?.method === PaymentMethod.Transfer ? ((webshop?.meta.transferSettings.creditor ? webshop?.meta.transferSettings.creditor : organization.meta.transferSettings.creditor) ?? organization.name) : ""
                }),
         */
        const data = [
            {
                title: $t(`%M7`),
                value: this.method ? Formatter.capitalizeFirstLetter(PaymentMethodHelper.getName(this.method)) : '',
            },
            {
                title: $t(`%m0`),
                value: this.status !== PaymentStatus.Succeeded ? Formatter.price(this?.price ?? 0) : Formatter.price(0),
            },
            ...(this.method === PaymentMethod.Transfer
                ? [
                    {
                        title: $t(`%J8`),
                        value: this.transferDescription ?? '',
                    },
                    {
                        title: $t(`%kc`),
                        value: this.transferSettings?.iban ?? '',
                    },
                    {
                        title: $t(`%J5`),
                        value: this.transferSettings?.creditor ?? '',
                    },
                ]
                : []),
        ];
        for (const replacement of data) {
            if (replacement.value.length === 0) {
                continue;
            }
            str += `<tr><td><h4>${Formatter.escapeHtml(replacement.title)}</h4></td><td>${Formatter.escapeHtml(replacement.value)}</td></tr>`;
        }
        return str + '</tbody></table>';
    }
    get priceBreakdown() {
        const breakdown = [{ name: $t('%2U'), price: this.price }];
        if (this.roundingAmount !== 0) {
            breakdown.unshift({
                name: $t('%1b6'),
                price: this.roundingAmount,
            });
        }
        return breakdown;
    }
    static roundPrice(price) {
        return STMath.round(price / 100) * 100;
    }
    static round(amount) {
        const rounded = Payment.roundPrice(amount);
        const difference = rounded - amount;
        if (difference === 0) {
            return {
                price: amount,
                roundingAmount: 0,
            };
        }
        if (difference > 100 || difference < -100) {
            throw new Error('Unexpected rounding difference of ' + difference + ' for price ' + amount.toString());
        }
        return {
            price: amount + difference,
            roundingAmount: difference,
        };
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Payment.prototype, "id", void 0);
__decorate([
    field({ decoder: new EnumDecoder(PaymentType), version: 353 })
], Payment.prototype, "type", void 0);
__decorate([
    field({ decoder: new EnumDecoder(PaymentMethodV150), nullable: true }),
    field({
        decoder: new EnumDecoder(PaymentMethod),
        version: 151,
        downgrade: downgradePaymentMethodV150,
    })
], Payment.prototype, "method", void 0);
__decorate([
    field({ decoder: new EnumDecoder(PaymentStatus) })
], Payment.prototype, "status", void 0);
__decorate([
    field({ decoder: new EnumDecoder(PaymentProvider), nullable: true, version: 152 })
], Payment.prototype, "provider", void 0);
__decorate([
    field({ decoder: PaymentCustomer, nullable: true, version: 321 })
], Payment.prototype, "customer", void 0);
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], Payment.prototype, "price", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 399 })
], Payment.prototype, "refundedAmount", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 399 })
], Payment.prototype, "roundingAmount", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 92 }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, nullable: true })
], Payment.prototype, "freeContribution", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], Payment.prototype, "transferDescription", void 0);
__decorate([
    field({ decoder: TransferSettings, nullable: true, version: 160 })
], Payment.prototype, "transferSettings", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], Payment.prototype, "paidAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Payment.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Payment.prototype, "updatedAt", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 324 })
], Payment.prototype, "organizationId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 344 })
], Payment.prototype, "payingOrganizationId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 392 })
], Payment.prototype, "invoiceId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 393 })
], Payment.prototype, "payingUserId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 401 })
], Payment.prototype, "reversingPaymentId", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 401 })
], Payment.prototype, "pendingRefundAmount", void 0);
export class SettlementReference extends AutoEncoder {
    id;
    reference;
    settledAt;
    amount;
    /**
     * @deprecated: moved to transferFee
     * Fee for the payment provider for the individual payment
     * Only set if it is witheld from the settlement/payout
     */
    fee = 0;
}
__decorate([
    field({ decoder: StringDecoder })
], SettlementReference.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], SettlementReference.prototype, "reference", void 0);
__decorate([
    field({ decoder: DateDecoder })
], SettlementReference.prototype, "settledAt", void 0);
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], SettlementReference.prototype, "amount", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 195 }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], SettlementReference.prototype, "fee", void 0);
export class PrivatePayment extends Payment {
    settlement = null;
    /**
     * All settlements this payment was part of (e.g. the payment in one payout, its refund in a
     * later one). Admin-only, stripped like the legacy settlement blob.
     */
    settlements = [];
    iban = null;
    ibanName = null;
    transferFee = 0;
    serviceFeePayout = 0;
    serviceFeeManual = 0;
    serviceFeeManualCharged = 0;
}
__decorate([
    field({ decoder: SettlementReference, nullable: true })
], PrivatePayment.prototype, "settlement", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(PaymentSettlementDetailed), version: 409 })
], PrivatePayment.prototype, "settlements", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 153 })
], PrivatePayment.prototype, "iban", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 153 })
], PrivatePayment.prototype, "ibanName", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 197 }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], PrivatePayment.prototype, "transferFee", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, optional: true })
], PrivatePayment.prototype, "serviceFeePayout", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, optional: true })
], PrivatePayment.prototype, "serviceFeeManual", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, optional: true })
], PrivatePayment.prototype, "serviceFeeManualCharged", void 0);
//# sourceMappingURL=Payment.js.map